import { DateOnly } from './date-only';
import { SECONDS_PER_DAY } from '../types/constants';
import { CalendarWeek } from '../types/main-content.types';

export function getLocalWeekDayFromDate(date: Date): string {
  return date.toLocaleDateString('en-US', { weekday: 'short' });
}

export function getLocalMonthDayFromDate(date: Date): string {
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

export function getForecastDayLabelForUsersTimezone(date: DateOnly, dayNumber: number): string {
  return `Day ${dayNumber} - ${date.weekdayShort} ${date.monthDayShort}`;
}

/**
 * Normalizes a UTC date-time string so the JS Date constructor can parse it.
 * Handles two API quirks: fractional seconds beyond millisecond precision (e.g. ".788021",
 * which Date silently fails to parse) and a missing UTC designator when no offset is present.
 */
function normalizeUtcDateString(utcDateString: string): string {
  const truncatedFraction = utcDateString.replace(/\.(\d{3})\d+/, '.$1');
  const hasOffset = /[+-]\d{2}:?\d{2}$/.test(truncatedFraction) || truncatedFraction.endsWith('Z');

  return hasOffset ? truncatedFraction : `${truncatedFraction}Z`;
}

/**
 * this exclusively for showing the correct forecast for init time
 * take a utc date string and converts it to local time and formats it for display in the UI when hovering over a point on the map
 * @param utcDateString init-time of a saved Source (2026-02-09T19:58:11)
 * @param withWeekday prepend the short weekday (e.g. "Mon"); pass false to omit it
 * @returns formatted local time string (e.g. Mon Feb 09 2:58 PM, or Feb 9 2:58 PM when withWeekday is false)
 */
export function formatUTCtoLocalTime(utcDateString: string = new Date().toISOString(), withWeekday = true): string {
  const date = new Date(normalizeUtcDateString(utcDateString));

  const time = date
    .toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true,
    })
    .toUpperCase();

  if (!withWeekday) {
    return `${getLocalMonthDayFromDate(date)} ${time}`;
  }

  const weekday = date.toLocaleDateString('en-US', { weekday: 'short' });
  const month = date.toLocaleDateString('en-US', { month: 'short' });
  const day = date.toLocaleDateString('en-US', { day: '2-digit' });

  return `${weekday} ${month} ${day} ${time}`;
}

export function formatTimestampToDate(timestamp: number, withHours = false): string {
  const date = new Date(timestamp);

  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');

  if (withHours) {
    const hours = String(date.getUTCHours()).padStart(2, '0');
    const minutes = String(date.getUTCMinutes()).padStart(2, '0');
    return `${year}-${month}-${day} ${hours}:${minutes}`;
  }

  return `${year}-${month}-${day}`;
}

/**
 * Converts a value to YYYY-MM-DD format for SQL queries.
 *
 * Accepts both legacy epoch-seconds numbers and "YYYY-MM-DD" strings — many
 * callers receive `params.day.value`, which the WXMK-1122 migration changed
 * from an epoch-seconds string to a date string. Passing a date string
 * through `new Date(value * 1000)` produces an Invalid Date and crashes
 * `toISOString()`, so we coerce defensively here.
 *
 * @param value - Unix timestamp in seconds OR a "YYYY-MM-DD" string
 * @returns ISO date string (YYYY-MM-DD)
 */
export function epochToDateString(value: number | string): string {
  return DateOnly.resolve(value).value;
}

//just formats the date to YYYY-MM-DD
export function getFormattedDate(date: Date = new Date()): string {
  return date.toISOString().split('T')[0];
}

export function getFormattedDateLocal(date: Date = new Date()): string {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

export function getFormattedUTCDate(date: Date = new Date()): string {
  const year = date.getUTCFullYear();
  const month = String(date.getUTCMonth() + 1).padStart(2, '0');
  const day = String(date.getUTCDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
}

// Day-of-year (1-366) for the UTC calendar date.
export function getDayOfYear(date: Date): number {
  const start = Date.UTC(date.getUTCFullYear(), 0, 0);
  const diff = Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate()) - start;
  return Math.floor(diff / SECONDS_PER_DAY / 1000);
}

export function getDateInSeconds(date: string): number {
  return new Date(date).getTime() / 1000;
}

export function daysBetweenDates(dateA?: string, dateB?: string): number {
  if (!dateA || !dateB) {
    return 0;
  }

  return Math.round(Math.abs(getDateInSeconds(dateA) - getDateInSeconds(dateB)) / SECONDS_PER_DAY);
}

export function snapToLocalMidnightEpoch(epochSeconds: number): number {
  const d = new Date(epochSeconds * 1000);
  d.setHours(0, 0, 0, 0);
  return Math.floor(d.getTime() / 1000);
}

export function getUserTimezone(): string {
  return Intl.DateTimeFormat().resolvedOptions().timeZone;
}

// create & return a start/end date epoch from the period range (e.g., "1-5" day or "8-14" day)
export function computeEpoch(baseStartEpoch: number, timeRange: string, startOrEndDay: 'start' | 'end'): number {
  const m = String(timeRange).match(/^\s*(\d+)\s*-\s*(\d+)\s*day\s*$/i);
  if (!m) {
    return startOrEndDay === 'start' ? baseStartEpoch : baseStartEpoch + SECONDS_PER_DAY;
  }

  const a = Number(m[1]);
  const b = Number(m[2]);
  if (!Number.isFinite(a) || !Number.isFinite(b) || a < 1 || b < a) {
    return startOrEndDay === 'start' ? baseStartEpoch : baseStartEpoch + SECONDS_PER_DAY;
  }

  const dayOffset = startOrEndDay === 'start' ? a - 1 : b;
  return baseStartEpoch + dayOffset * SECONDS_PER_DAY;
}

export function dateStringToUtcNoonEpoch(dateStr: string): number {
  return DateOnly.fromString(dateStr).toEpochSeconds();
}

export function addDaysToDateString(dateStr: string, days: number): string {
  return DateOnly.fromString(dateStr).addDays(days).value;
}

export function todayAsDateString(): string {
  return DateOnly.today().value;
}

export function dayValueToDateString(value: string | number | undefined | null): string {
  return DateOnly.resolve(value).value;
}

export function hasTimePassedLessThanMinutes(utcDateTime: string, minutes: number): boolean {
  if (minutes < 0) {
    return false;
  }

  const initTimeMs = new Date(normalizeUtcDateString(utcDateTime)).getTime();

  if (Number.isNaN(initTimeMs)) {
    return false;
  }

  const elapsedMs = Date.now() - initTimeMs;
  const maxElapsedMs = minutes * 60 * 1000;
  return elapsedMs >= 0 && elapsedMs <= maxElapsedMs;
}

export function getMonthShort(month: number): string {
  return new Date(Date.UTC(2000, month - 1, 1)).toLocaleDateString('default', {
    month: 'short',
    timeZone: 'UTC',
  });
}

/**
 * Monday of the ISO week containing the given date.
 * Returned Date is snapped to UTC noon so subsequent arithmetic is DST-safe.
 */
export function getMondayOfWeek(date: Date): Date {
  return DateOnly.fromLocalDate(date).mondayOfWeek().toUtcNoonDate();
}

export function getISOWeekInfo(date: Date): CalendarWeek {
  return getISOWeekInfoFromMonday(DateOnly.fromLocalDate(date).mondayOfWeek());
}

export function getISOWeekInfoFromMonday(monday: DateOnly): CalendarWeek {
  const sunday = monday.addDays(6);
  const thursday = monday.addDays(3);

  const isoYear = thursday.year;
  const week1Monday = DateOnly.fromString(`${isoYear}-01-04`).mondayOfWeek();

  const weekNo = Math.floor(thursday.diffDays(week1Monday) / 7) + 1;
  const fmt = (d: DateOnly) =>
    d.toUtcNoonDate().toLocaleDateString('en-GB', { month: 'short', day: 'numeric', timeZone: 'UTC' });
  return {
    week: weekNo,
    year: isoYear,
    startDate: Date.UTC(monday.year, monday.month - 1, monday.day) / 1000,
    endDate: Date.UTC(sunday.year, sunday.month - 1, sunday.day) / 1000,
    label: `Calendar Week ${weekNo} (${fmt(monday)} – ${fmt(sunday)})`,
  };
}
